import { useRef, useState, type TouchEvent, type CSSProperties } from "react";

// Port of swipeDismiss.ts's gesture math, scoped to a single dialog's Paper
// element instead of a global document listener + bootstrap.Modal lookup -
// MUI Dialogs aren't bootstrap.Modal instances, so the original generic
// swipeDismiss.ts silently stopped covering any dialog migrated to
// AppDialog. Same thresholds/behavior, applied via touch handlers spread
// directly onto the dialog's Paper.
const DRAG_HANDLE_ZONE_PX = 60;
const DISMISS_THRESHOLD_PX = 110;
const BOTTOM_SHEET_BREAKPOINT = "(max-width: 576px)";

export function useSwipeDismiss(onDismiss: () => void) {
  const [style, setStyle] = useState<CSSProperties>({});
  const dragging = useRef(false);
  const startY = useRef(0);
  const deltaY = useRef(0);

  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    if (!window.matchMedia(BOTTOM_SHEET_BREAKPOINT).matches) return;
    const touch = e.touches[0];
    if (!touch) return;

    const rect = e.currentTarget.getBoundingClientRect();
    if (touch.clientY - rect.top > DRAG_HANDLE_ZONE_PX) return; // only the grab-handle zone starts a drag

    dragging.current = true;
    startY.current = touch.clientY;
    deltaY.current = 0;
    setStyle({ transition: "none" });
  };

  const onTouchMove = (e: TouchEvent<HTMLElement>) => {
    if (!dragging.current) return;
    const touch = e.touches[0];
    if (!touch) return;

    deltaY.current = Math.max(0, touch.clientY - startY.current);
    setStyle({ transition: "none", transform: `translateY(${deltaY.current}px)` });
  };

  const onTouchEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;

    const shouldDismiss = deltaY.current > DISMISS_THRESHOLD_PX;
    setStyle({});
    if (shouldDismiss) onDismiss();
  };

  return { style, onTouchStart, onTouchMove, onTouchEnd };
}
